import axios from "axios";
import { debounce } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactGA from "react-ga";
import { useIntl } from "react-intl";
import { AdSettings } from "../types/ads";
import { SurveyData } from "../types/survey";
import { addImpressionTags } from "../utils/adUtils";
import { getContrastingColor } from "../utils/color";
import { detectDeviceType } from "../utils/device";
import { loadMedia } from "../utils/media";
import { pathAPI } from "../utils/pathAPI";
import { getQueryString } from "../utils/query";
import { LoadingSpinner } from "./common/LoadingSpinner";
import { MediaSkeleton } from "./common/MediaSkeleton"; // Import MediaSkeleton
import IntroScreen from "./IntroScreen";
import Survey2 from "./Survey2";
import Survey from "./Survey";
import settings from "../settings";
// import AdvancedTracking from "../utils/advancedTracking";

interface AdRendererProps {
  adSettings: AdSettings | null;
  macAddressClient: string;
  onSubmit: () => void;
  onSubmitSurvey: (surveyData: SurveyData) => void;
}

const AdRenderer: React.FC<AdRendererProps> = ({
  adSettings,
  macAddressClient,
  onSubmit,
  onSubmitSurvey,
}) => {
  const [contentCountdown, setContentCountdown] = useState<number | null>(null);
  const isCountingDown = useMemo(() => contentCountdown === null || contentCountdown > 0, [contentCountdown]);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [bannerUrl, setBannerUrl] = useState<string>("");
  const intl = useIntl();
  const [imageTabletUrl, setImageTabletUrl] = useState<string>("");
  const [imageDesktopUrl, setImageDesktopUrl] = useState<string>("");
  const [responsiveImageUrl, setResponsiveImageUrl] = useState<string>(""); // New state for dynamic image URL
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [isImageLoading, setIsImageLoading] = useState<boolean>(false);
  const [isVideoLoading, setIsVideoLoading] = useState<boolean>(false);
  const [isLogoLoading, setIsLogoLoading] = useState<boolean>(false);
  const [pageEntryTime] = useState<Date>(new Date());
  const [showMainContent, setShowMainContent] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const isMounted = useRef(true);
  // const [showPlayButton, setShowPlayButton] = useState(false); // State để hiển thị nút phát thủ công trên iOS
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasTrackedPageLoad = useRef(false);
  // const advancedTrackingRef = useRef<AdvancedTracking | null>(null);
  // const hasInitializedTracking = useRef(false); // Flag để đảm bảo chỉ init 1 lần

  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownStartTimeRef = useRef<number | null>(null);

  const [surveyData, setSurveyData] = useState({
    fullName: "",
    phoneNumber: "",
    email: "",
    birthdate: "",
    gender: "",
    optionalSurveyQuestion: "",
    optionalSurveyAnswer: "",
    optionalSurveyQuestion1: "",
    optionalSurveyAnswer1: "",
    optionalSurveyAnsExtend: "",
    isSendOTP: "false",
  });

  const [otpData, setOtpData] = useState({ otp: "" });
  const [showSurvey, setShowSurvey] = useState(false);

  const loadImage = (imgPath?: string) =>
    loadMedia({
      path: imgPath,
      setUrl: setImageUrl,
      setLoading: setIsImageLoading,
    });

  const loadImageBanner = (imgPath?: string) =>
    loadMedia({
      path: imgPath,
      setUrl: setBannerUrl,
      setLoading: setIsImageLoading,
    });

  const loadImageTablet = (imgPath?: string) =>
    loadMedia({
      path: imgPath,
      setUrl: setImageTabletUrl,
      setLoading: setIsImageLoading,
    });

  const loadImageDesktop = (imgPath?: string) =>
    loadMedia({
      path: imgPath,
      setUrl: setImageDesktopUrl,
      setLoading: setIsImageLoading,
    });

  const loadVideo = (videoPath?: string) =>
    loadMedia({
      path: videoPath,
      setUrl: setVideoUrl,
      setLoading: setIsVideoLoading,
    });

  const loadLogo = (logoPath?: string) =>
    loadMedia({
      path: logoPath,
      setUrl: setLogoUrl,
      setLoading: setIsLogoLoading,
      elementId: "logoPartner",
      conditionRender: showMainContent,
    });

  const handleClickWithTag = async (
    clickTag: string | undefined,
    isDependence?: boolean,
  ) => {
    try {
      // Gọi hàm onSubmit để tiếp tục luồng Captive Portal
      if (isDependence) {
        onSubmit();
      }

      // Ghi log sự kiện nhấp chuột
      handleDataTracking(1, "click", "User clicked third-party ad");
      ReactGA.event({
        category: "Button",
        action: "Third Party Ad Click",
        label: clickTag || "unknown",
      });

      // Xử lý click tag nếu có
      if (clickTag) {
        // Thay thế các tham số động
        const finalClickTag = clickTag
          .replace("${GDPR}", "0") // Thay đổi theo logic GDPR thực tế
          .replace("${GDPR_CONSENT_755}", ""); // Thay đổi theo logic GDPR thực tế

        // Gửi yêu cầu đến URL click tag
        await fetch(finalClickTag, {
          method: "GET",
          mode: "no-cors", // Sử dụng no-cors để tránh lỗi CORS
        }).catch((error) => {
          console.error("Failed to send click tag request:", error);
          // Có thể hiển thị thông báo cho người dùng nếu cần
          // alert(intl.formatMessage({ id: "adRenderer.adBlockWarning" }));
        });
      }
    } catch (error) {
      console.error("Error handling click tag:", error);
    }
  };
  useEffect(() => {
    if (showMainContent && adSettings) {
      // Chèn các thẻ theo dõi hiển thị khi quảng cáo được hiển thị
      const impressionTagImage = adSettings.impression_tag_3rd_party_image;
      const impressionTagFrame = adSettings.impression_tag_3rd_party_iframe;
      const impressionTagJs = adSettings.impression_tag_3rd_party_js;

      addImpressionTags(
        impressionTagImage,
        impressionTagFrame,
        impressionTagJs,
      );

      ReactGA.event({
        category: "Ad",
        action: "Third Party Ad Displayed",
        label: adSettings?.impression_tag_3rd_party_image || "unknown",
      });
    }
  }, [showMainContent, adSettings]);

  useEffect(() => {
    if (!adSettings) return;

    console.log({ adSettings });

    const {
      ad_type,
      image_url,
      image_tablet_url,
      image_desktop_url,
      banner_url,
      video_url,
      logo_img_url,
    } = adSettings;

    const typesToLoadImage = ["banner", "survey", "survey2"];
    const typesToLoadVideo = ["video2", "video1"];
    const typesToLoadBanner = ["video2", "app"];

    if (ad_type && typesToLoadImage.includes(ad_type)) {
      loadImage(image_url);
      loadImageTablet(image_tablet_url);
      loadImageDesktop(image_desktop_url);
    }

    if (ad_type && typesToLoadBanner.includes(ad_type)) {
      loadImageBanner(banner_url);
    }

    if (ad_type && typesToLoadVideo.includes(ad_type)) {
      loadVideo(video_url);
    }

    loadLogo(logo_img_url);

    ReactGA.event({
      category: "Ad",
      action: "Ad Loaded",
      label: ad_type || "default",
    });
  }, [adSettings, showMainContent]);

  useEffect(() => {
    if (isMounted.current) {
      // Enable 3rd party tracking cho TẤT CẢ ad types (không check is_enable_3rd_party nữa)
      if (!showMainContent || !adSettings?.impression_tag_3rd_party_js) return;

      const strJs = adSettings?.impression_tag_3rd_party_js;
      const matchJS = strJs?.match(/src\s*=\s*"([^"]+)"|alt\s*=\s*"([^"]+)"/gi);
      let jsSrc = "";
      if (matchJS) {
        matchJS.forEach((attr) => {
          if (/src\s*=/i.test(attr)) {
            const srcMatch = attr?.match(/"([^"]+)"/);
            if (srcMatch && srcMatch[1]) {
              jsSrc = srcMatch[1];
            }
          }
        });
      }

      if (!jsSrc) return; // Không có src thì không load script

      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = jsSrc;
      script.setAttribute("language", "JavaScript1.1");
      script.setAttribute("attributionsrc", ""); // boolean‑like attr
      document.body.appendChild(script);
      isMounted.current = false;
      // cleanup
      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [showMainContent, adSettings?.impression_tag_3rd_party_js]);

  // Initialize Advanced Tracking - CHỈ INIT MỘT LẦN khi có adSettings
  // useEffect(() => {
  //   const initTracking = async () => {
  //     // Chỉ init một lần - kiểm tra flag
  //     if (hasInitializedTracking.current || !adSettings) return;

  //     hasInitializedTracking.current = true; // Đánh dấu đã init

  //     const tracking = new AdvancedTracking({
  //       adId: adSettings.id,
  //       adType: adSettings.ad_type || "default",
  //       ssid: getQueryString("ssidName") || "",
  //       apMac: getQueryString("apMac") || undefined,
  //       macAddressClient: macAddressClient,
  //       backendUrl: process.env.REACT_APP_BACKEND_URL_TEST || "",
  //       enableBehaviorTracking: true,
  //       enableWifiAnalytics: true,
  //       enableDeviceFingerprinting: true,
  //     });

  //     await tracking.init();
  //     advancedTrackingRef.current = tracking;
  //     console.log('✅ Advanced Tracking initialized');
  //   };

  //   initTracking();

  //   // Cleanup CHỈ khi component unmount
  //   return () => {
  //     if (advancedTrackingRef.current) {
  //       console.log('🔚 Cleaning up Advanced Tracking on unmount');
  //       advancedTrackingRef.current.endTracking();
  //       advancedTrackingRef.current = null;
  //     }
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [adSettings]); // Chạy khi adSettings có giá trị, nhưng chỉ init 1 lần nhờ flag

  useEffect(() => {
    if (!hasTrackedPageLoad.current) {
      handleDataTracking(0, "page_load", "User loaded the page");
      hasTrackedPageLoad.current = true;
      ReactGA.event({
        category: "Page",
        action: "Page Load",
        label: "AdRenderer",
      });
    }
  }, []);

  // Đồng bộ thời gian đếm ngược từ cấu hình quảng cáo nếu có (ví dụ: non_skip = 10 giây)
  useEffect(() => {
    const isVideoType = ["video1", "video2"].includes(adSettings?.ad_type as string);
    if (!isVideoType && adSettings?.non_skip != null && adSettings.non_skip > 0) {
      setContentCountdown(adSettings.non_skip);
    }
  }, [adSettings?.id, adSettings?.non_skip, adSettings?.ad_type]);

  // Tách biệt việc theo dõi sự kiện GA khi hoàn thành đếm ngược
  useEffect(() => {
    if (contentCountdown === 0 && showMainContent) {
      const countdownAdTypes = [
        "banner",
        "app",
        "survey",
        "survey2",
        "3rd_party",
        undefined,
      ];
      if (countdownAdTypes.includes(adSettings?.ad_type)) {
        ReactGA.event({
          category: "Ad",
          action: "Countdown Completed",
          label: adSettings?.ad_type || "default",
        });
      }
    }
  }, [contentCountdown === 0, showMainContent]);

  useEffect(() => {
    const isVideoType = ["video1", "video2"].includes(
      adSettings?.ad_type as string,
    );
    if (!showMainContent || isVideoType) return;

    const duration = adSettings?.non_skip ?? 3;
    countdownStartTimeRef.current = Date.now();
    setContentCountdown(duration);

    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

    const intervalId = setInterval(() => {
      const elapsed = Math.floor((Date.now() - countdownStartTimeRef.current!) / 1000);
      const remaining = Math.max(duration - elapsed, 0);
      setContentCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(intervalId);
        countdownTimerRef.current = null;
      }
    }, 500);
    countdownTimerRef.current = intervalId;

    return () => {
      clearInterval(intervalId);
      countdownTimerRef.current = null;
    };
  }, [showMainContent, adSettings?.ad_type, adSettings?.non_skip]);


  const handleVideoTimeUpdate = () => {
    if (!videoRef.current) return;

    // Lần đầu onTimeUpdate fire = video đang hiển thị và phát thật → bắt đầu đếm
    if (countdownStartTimeRef.current === null) {
      const duration = adSettings?.non_skip || 5;
      countdownStartTimeRef.current = Date.now();
      setContentCountdown(duration);

      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      const intervalId = setInterval(() => {
        const elapsed = Math.floor((Date.now() - countdownStartTimeRef.current!) / 1000);
        const remaining = Math.max(duration - elapsed, 0);
        setContentCountdown(remaining);
        if (remaining <= 0) {
          clearInterval(intervalId);
          countdownTimerRef.current = null;
        }
      }, 500);
      countdownTimerRef.current = intervalId;
    }

    // GA tracking
    const currentTime = Math.floor(videoRef.current.currentTime);
    if (currentTime > 0 && currentTime % 5 === 0) {
      ReactGA.event({
        category: "Video",
        action: "Video Play Time",
        label: `${adSettings?.ad_type}: ${currentTime}s`,
        value: currentTime,
      });
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setSurveyData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name == "otp") {
      setOtpData((prev) => ({ ...prev, [name]: value }));
    }
    ReactGA.event({
      category: "Survey",
      action: "Input Changed",
      label: `Field: ${name}`,
    });
  };

  // Function to determine the responsive image URL based on screen size
  const getResponsiveImageUrl = () => {
    const screenWidth = window.innerWidth;
    const mobileBreakpoint = 768; // Mobile: < 768px
    const tabletBreakpoint = 1025; // Tablet: >= 768px and < 1024px

    if (screenWidth >= tabletBreakpoint && imageDesktopUrl) {
      return imageDesktopUrl; // Desktop
    } else if (screenWidth >= mobileBreakpoint && imageTabletUrl) {
      return imageTabletUrl; // Tablet
    } else if (imageUrl) {
      return imageUrl; // Mobile or fallback
    } else {
      return settings.defaultBanner; // Default fallback
    }
  };

  useEffect(() => {
    const updateDeviceType = debounce(() => {
      // const screenWidth = window.innerWidth;
      // const mobileBreakpoint = 768;
      // const tabletBreakpoint = 1024;
      const updateImageUrl = () => {
        setResponsiveImageUrl(getResponsiveImageUrl());
      };

      // Set initial image URL
      updateImageUrl();
    }, 200);

    updateDeviceType();
    window.addEventListener("resize", updateDeviceType);
    return () => {
      window.removeEventListener("resize", updateDeviceType);
      updateDeviceType.cancel();
    };
  }, [imageUrl, imageDesktopUrl, imageTabletUrl]);

  useEffect(() => {
    if (showMainContent && videoUrl && !isVideoLoading && videoRef.current) {
      const isIOS = detectDeviceType() === "iphone";
      const videoElement = videoRef.current;
      videoElement.muted = true; // Ensure muted for initial autoplay

      const playVideo = async () => {
        try {
          await videoElement.play();
          // setShowPlayButton(false);
          ReactGA.event({
            category: "Video",
            action: "Automatic Play",
            label: adSettings?.ad_type || "unknown",
          });

          // Unmute after 1 second
          const unmuteTimer = setTimeout(() => {
            videoElement.muted = false;
            ReactGA.event({
              category: "Video",
              action: "Unmuted",
              label: adSettings?.ad_type || "unknown",
            });
          }, 100);

          // Cleanup timer if component unmounts or video changes
          return () => clearTimeout(unmuteTimer);
        } catch (error) {
          console.error("Error attempting to play video:", error);
          if (isIOS) {
            // setShowPlayButton(true); // Show manual play button on iOS
          }
        }
      };

      playVideo();
    }
  }, [videoUrl, isVideoLoading, showMainContent, adSettings?.ad_type]);

  const sendTrackingData = async (eventData: any) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL_TEST + pathAPI.trackActivity}`,
        eventData,
        {
          headers: {
            "Content-Type": "application/json;charset=utf-8",
            "Access-Control-Allow-Origin": "https://wifi.vtctelecom.com.vn",
            "Access-Control-Allow-Methods": "POST, GET, OPTIONS",
            "Access-Control-Allow-Headers": "Content-Type, Authorization",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Failed to send tracking data:", err);
    }
  };

  const handleDataTracking = (
    clickNumber: number,
    activityType: string,
    activityDetails: string,
  ) => {
    // Use advanced tracking if available
    // if (advancedTrackingRef.current) {
    //   advancedTrackingRef.current.trackEvent(activityType, {
    //     click_number: clickNumber,
    //     activityDetails,
    //   });
    // }

    // Fallback to old tracking for backward compatibility
    if (adSettings) {
      const activity = {
        click_number: clickNumber,
        adId: adSettings?.id,
        adType: adSettings?.ad_type || "default",
        macAddressClient: getQueryString("clientMac"),
        activityType: activityType,
        activityDetails: activityDetails,
        pageEntryTime: pageEntryTime,
        currentDate: new Date(),
      };
      sendTrackingData(activity);
    }
  };

  const renderDefault = () => (
    <div className="relative w-[100vw] xl:w-full h-full overflow-hidden">
      <div
        className={`flex items-center xl:flex-none overflow-hidden h-full transition-all duration-500 ease-in-out ${
          showMainContent
            ? "opacity-100 translate-x-0"
            : "opacity-0 translate-x-full pointer-events-none"
        }`}
        style={{ backgroundColor: adSettings?.background_color }}
      >
        {isImageLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="flex flex-col w-full xl:flex-none xl:grid xl:grid-cols-3 px-5 gap-5">
            <div className="xl:col-span-2 w-full">
              <img
                src={settings.defaultBanner}
                alt="Default Banner"
                className="w-full object-contain relative"
              />
            </div>
            <div className="xl:col-span-1 w-full">
              <div className="w-full flex justify-center items-center h-full bottom-5">
                <button
                  onClick={() => {
                    onSubmit();
                    handleDataTracking(1, "click", "User click access");
                    ReactGA.event({
                      category: "Button",
                      action: "Connect Internet",
                      label: "Default Ad",
                    });
                  }}
                  disabled={isCountingDown}
                  className="relative w-full max-w-xs px-8 py-3 rounded-full font-bold uppercase text-lg tracking-wide text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95 shadow-md group overflow-hidden"
                  style={{
                    backgroundColor: adSettings?.button_color || "#e60000",
                    color: getContrastingColor(
                      adSettings?.button_color || "#e60000",
                    ),
                  }}
                >
                  {(intl.locale === "en"
                    ? adSettings?.button_text_en
                    : adSettings?.button_text) ||
                    intl.formatMessage({ id: "adRenderer.connectInternet" })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderBanner = () => (
    <div
      className="w-full h-full flex flex-col xl:flex-row overflow-hidden"
      style={{ backgroundColor: adSettings?.background_color }}
    >
      {/* Media Section */}
      <div className="flex-1 min-h-0 flex flex-col justify-center items-center relative">
        {isImageLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-full h-full md:h-auto aspect-video shadow-sm overflow-hidden max-h-full bg-gray-200">
              <MediaSkeleton className="bg-transparent" />
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center overflow-hidden slide-in-right">
            <img
              src={responsiveImageUrl || require("../assets/company_logo.jpg")}
              alt="Banner"
              className="w-full h-full object-contain max-h-full"
            />
          </div>
        )}
      </div>

      {/* Button Section */}
      <div className="w-full xl:w-[450px] flex-shrink-0 p-4 md:p-8 flex flex-col items-center justify-center xl:border-l xl:border-gray-100/10 bg-white/5">
        <button
          onClick={async () => {
            handleDataTracking(1, "click", "User click access");
            // 3rd party click tracking cho TẤT CẢ ad types
            await handleClickWithTag(
              adSettings?.impression_tag_3rd_party_click,
              false,
            );
            onSubmit();
            ReactGA.event({
              category: "Button",
              action: "Connect Internet",
              label: "Banner Ad",
            });
          }}
          disabled={isCountingDown}
          className="w-full max-w-sm px-5 py-3 md:px-8 md:py-4 rounded-full font-bold uppercase text-base md:text-lg tracking-wide disabled:opacity-75 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300 animated-button"
          style={{
            backgroundColor: adSettings?.button_color || "#0065b2",
            color: getContrastingColor(adSettings?.button_color || "#0065b2"),
          }}
        >
          {(intl.locale === "en"
            ? adSettings?.button_text_en
            : adSettings?.button_text) ||
            intl.formatMessage({ id: "adRenderer.connectInternet" })}
          {contentCountdown != null && contentCountdown > 0 && ` (${contentCountdown}s)`}
        </button>
      </div>
    </div>
  );

  const renderVideo = (type: "video1" | "video2") => {
    const isVideo1 = type === "video1";

    return (
      <div
        className="w-full h-full flex flex-col xl:flex-row overflow-hidden"
        style={{ backgroundColor: adSettings?.background_color }}
      >
        {/* Media Section */}
        <div
          className={`flex-1 min-h-0 flex ${
            isVideo1
              ? "flex-col justify-center"
              : "flex-col md:flex-row justify-center"
          }`}
        >
          {/* Video Container */}
          <div
            className={`relative flex items-center justify-center overflow-hidden ${
              isVideo1 ? "w-full h-full" : "w-full h-1/2 md:w-1/2 md:h-full"
            }`}
          >
            {isVideoLoading || !videoUrl ? (
              <div className="w-full h-full flex items-center justify-center p-4">
                <div className="w-full aspect-video shadow-sm rounded-xl overflow-hidden max-h-full bg-gray-200">
                  <MediaSkeleton className="bg-transparent" />
                </div>
              </div>
            ) : (
              <video
                ref={videoRef}
                src={videoUrl}
                preload="auto"
                muted
                playsInline
                loop
                onTimeUpdate={handleVideoTimeUpdate}
                onLoadedMetadata={() => {
                  videoRef.current?.play();
                  const activity = {
                    click_number: 0,
                    adId: adSettings?.id,
                    adType: adSettings?.ad_type,
                    macAddressClient: getQueryString("clientMac"),
                    activityType: "watch_ads",
                    activityDetails: "User watch ads",
                    pageEntryTime: pageEntryTime,
                    currentDate: new Date(),
                  };
                  sendTrackingData(activity);
                  ReactGA.event({
                    category: "Video",
                    action: "Video Started",
                    label: isVideo1 ? "Video1" : "Video2",
                  });
                }}
                className="w-full h-full object-contain max-h-full"
              />
            )}
          </div>

          {/* Video2 Banner Section */}
          {!isVideo1 && (
            <div className="w-full h-1/2 md:w-1/2 md:h-full flex items-center justify-center overflow-hidden slide-in-right">
              {isImageLoading ? (
                <div className="w-full aspect-video shadow-sm rounded-xl overflow-hidden max-h-full bg-gray-200">
                  <MediaSkeleton className="bg-transparent" />
                </div>
              ) : (
                <img
                  src={bannerUrl}
                  alt="Banner"
                  className="w-full h-full object-contain max-h-full"
                />
              )}
            </div>
          )}
        </div>

        {/* Button Section */}
        <div className="w-full xl:w-[450px] flex-shrink-0 p-4 md:p-8 flex flex-col items-center justify-center xl:border-l xl:border-gray-100/10 bg-white/5">
          <button
            onClick={async () => {
              handleDataTracking(1, "click", "User click access");
              // 3rd party click tracking cho TẤT CẢ ad types
              await handleClickWithTag(
                adSettings?.impression_tag_3rd_party_click,
                false,
              );
              onSubmit();
            }}
            disabled={isCountingDown}
            className="w-full max-w-sm px-5 py-3 md:px-8 md:py-4 rounded-full font-bold uppercase text-base md:text-lg tracking-wide disabled:opacity-75 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300 animated-button"
            style={{
              backgroundColor: adSettings?.button_color || "#e60000",
              color: getContrastingColor(adSettings?.button_color || "#e60000"),
            }}
          >
            {(intl.locale === "en"
              ? adSettings?.button_text_en
              : adSettings?.button_text) ||
              intl.formatMessage({ id: "adRenderer.connectInternet" })}
            {contentCountdown != null && contentCountdown > 0 && ` (${contentCountdown}s)`}
          </button>
        </div>
      </div>
    );
  };

  const renderThirdParty = () => {
    const strImage = adSettings?.impression_tag_3rd_party_image;
    const strIframe = adSettings?.impression_tag_3rd_party_iframe;
    const match = strImage?.match(/src\s*=\s*"([^"]+)"|alt\s*=\s*"([^"]+)"/gi);
    const matchIframe = strIframe?.match(
      /src\s*=\s*"([^"]+)"|alt\s*=\s*"([^"]+)"/gi,
    );

    let imageSrc = "";
    let imageAlt = "";
    let iframeSrc = "";
    if (match) {
      match.forEach((attr) => {
        if (/src\s*=/i.test(attr)) {
          const srcMatch = attr?.match(/"([^"]+)"/);
          if (srcMatch && srcMatch[1]) {
            imageSrc = srcMatch[1];
          }
        }
        if (/alt\s*=/i.test(attr)) {
          const altMatch = attr?.match(/"([^"]+)"/);
          if (altMatch && altMatch[1]) {
            imageAlt = altMatch[1];
          }
        }
      });
    }

    if (matchIframe) {
      matchIframe.forEach((attr) => {
        if (/src\s*=/i.test(attr)) {
          const srcMatch = attr?.match(/"([^"]+)"/);
          if (srcMatch && srcMatch[1]) {
            iframeSrc = srcMatch[1];
          }
        }
      });
    }
    return (
      <div>
        <div className="relative w-[100vw] xl:w-full h-full overflow-hidden">
          <div
            className={`relative top-0 left-0 w-full h-full flex flex-col transition-all duration-500 ease-in-out ${
              showMainContent
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-full pointer-events-none"
            }`}
            style={{ backgroundColor: adSettings?.background_color }}
          >
            <div className="w-full xl:py-4">
              {isImageLoading ? (
                <LoadingSpinner />
              ) : (
                <div className="flex flex-col w-full xl:flex-none xl:grid xl:grid-cols-3">
                  <div className="xl:col-span-2 w-full xl:mx-4">
                    {/* Luôn hiển thị 3rd party tracking cho TẤT CẢ ad types */}
                    {imageSrc && <img src={imageSrc} alt={imageAlt} />}
                    {iframeSrc && (
                      <iframe
                        src={iframeSrc}
                        className="w-full h-[calc(100vh-100px)] md:h-[calc(100vh-200px)]"
                        frameBorder="0"
                        title="Third Party Ad"
                        onLoad={() => {
                          handleDataTracking(
                            0,
                            "third_party_ad_load",
                            "Third party ad loaded",
                          );
                          ReactGA.event({
                            category: "Ad",
                            action: "Third Party Ad Loaded",
                            label: iframeSrc || "unknown",
                          });
                        }}
                      />
                    )}
                    {!imageSrc && !iframeSrc && (
                      <div className="text-center text-gray-500">
                        {intl.formatMessage({
                          id: "adRenderer.noThirdPartyContent",
                        })}
                      </div>
                    )}
                  </div>
                  <div className="xl:col-span-1 w-full px-4 xl:px-0">
                    <div className="w-full flex justify-center items-center h-full bottom-5">
                      <button
                        onClick={() =>
                          handleClickWithTag(
                            adSettings?.impression_tag_3rd_party_click,
                            true,
                          )
                        }
                        disabled={isCountingDown}
                        className="w-full max-w-sm px-6 py-3 rounded-full font-bold uppercase text-lg tracking-wide disabled:opacity-75 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300"
                        style={{
                          backgroundColor:
                            adSettings?.button_color || "#e60000",
                          color: getContrastingColor(
                            adSettings?.button_color || "#e60000",
                          ),
                        }}
                      >
                        {intl.locale === "en"
                          ? adSettings?.button_text_en
                          : adSettings?.button_text}
                        {contentCountdown != null && contentCountdown > 0 && ` (${contentCountdown}s)`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAppInstallPopup = () => {
    const deviceType = detectDeviceType();

    const getRedirectUrl = (): string | undefined => {
      if (deviceType === "iphone" && adSettings?.app_store_url) {
        return adSettings.app_store_url;
      } else if (
        (deviceType === "android" || deviceType === "windows") &&
        adSettings?.ch_play_url
      ) {
        return adSettings.ch_play_url;
      } else {
        return adSettings?.ch_play_url || adSettings?.destination_url;
      }
    };

    const redirectUrl = getRedirectUrl();

    console.log({ ad: intl.locale });
    return (
      <div className="h-full w-full overflow-hidden">
        <div
          className="flex flex-col items-center justify-center w-full h-full"
          style={{ backgroundColor: adSettings?.background_color || "#f5a623" }}
        >
          <div className="w-full flex-1 flex flex-col items-center justify-center overflow-hidden max-h-[60vh] p-4">
            {isImageLoading ? (
              <LoadingSpinner />
            ) : (
              <img
                src={bannerUrl || require("../assets/company_logo.jpg")}
                alt="App Install Banner"
                className="w-full h-full object-contain"
              />
            )}
          </div>
          <div className="my-5 px-5 w-full text-center">
            <button
              onClick={async () => {
                handleDataTracking(
                  1,
                  "click",
                  `User clicked app install link for ${deviceType}`,
                );
                // 3rd party click tracking cho TẤT CẢ ad types
                await handleClickWithTag(
                  adSettings?.impression_tag_3rd_party_click,
                  false,
                );
                ReactGA.event({
                  category: "Button",
                  action: "Install App",
                  label: `Device: ${deviceType}`,
                });
                if (redirectUrl) {
                  window.location.href = redirectUrl;
                }
              }}
              disabled={isCountingDown || !redirectUrl}
              className="w-full max-w-sm px-8 py-3 rounded-full font-bold uppercase text-lg tracking-wide disabled:opacity-75 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300 animated-button"
              style={{
                backgroundColor: adSettings?.button_color || "#e60000",
                color: getContrastingColor(
                  adSettings?.button_color || "#e60000",
                ),
              }}
            >
              {(intl.locale === "en"
                ? adSettings?.button_text_en
                : adSettings?.button_text) || "CÀI ĐẶT NGAY"}{" "}
              {contentCountdown != null && contentCountdown > 0 && ` (${contentCountdown}s)`}
            </button>
          </div>
          <div className="w-full mt-4 flex justify-center px-5">
            <button
              onClick={async () => {
                handleDataTracking(1, "click", "User skipped app install");
                // 3rd party click tracking cho TẤT CẢ ad types
                await handleClickWithTag(
                  adSettings?.impression_tag_3rd_party_click,
                  false,
                );
                onSubmit();
                ReactGA.event({
                  category: "Button",
                  action: "Skip App Install",
                  label: `Device: ${deviceType}`,
                });
              }}
              className="w-full max-w-xs rounded-md py-2 font-bold uppercase text-lg tracking-wide text-white"
            >
              BỎ QUA
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!showMainContent) {
    return (
      <IntroScreen
        showMainContent={showMainContent}
        setShowMainContent={setShowMainContent}
        showTermsModal={showTermsModal}
        setShowTermsModal={setShowTermsModal}
        isLogoLoading={isLogoLoading}
        logoUrl={logoUrl}
        adSettings={adSettings}
      />
    );
  } else if (!adSettings) {
    return renderDefault();
  } else {
    switch (adSettings.ad_type) {
      case "banner":
        return renderBanner();
      case "video1":
        return renderVideo("video1");
      case "video2":
        return renderVideo("video2");
      case "survey":
        return (
          <Survey
            showSurvey={showSurvey}
            adSettings={adSettings}
            ReactGA={ReactGA}
            onDataTracking={handleDataTracking}
            onSubmit={onSubmit}
            onSubmitSurvey={onSubmitSurvey}
            isLogoLoading={isLogoLoading}
            onInputChange={handleInputChange}
            responsiveImageUrl={responsiveImageUrl}
            isImageLoading={isImageLoading}
            contentCountdown={contentCountdown ?? 0}
            surveyData={surveyData}
            imageDesktopUrl={imageDesktopUrl}
            imageTabletUrl={imageTabletUrl}
            imageUrl={imageUrl}
            showMainContent={showMainContent}
            setShowSurvey={setShowSurvey}
          />
        );
      case "app":
        return renderAppInstallPopup();
      case "3rd_party":
        return renderThirdParty();
      case "survey2":
        return (
          <Survey2
            showSurvey={showSurvey}
            adSettings={adSettings}
            ReactGA={ReactGA}
            onDataTracking={handleDataTracking}
            onClickWithTag={handleClickWithTag}
            logoUrl={logoUrl}
            onSubmit={onSubmit}
            onSubmitSurvey={onSubmitSurvey}
            isLogoLoading={isLogoLoading}
            onInputChange={handleInputChange}
            responsiveImageUrl={responsiveImageUrl}
            isImageLoading={isImageLoading}
            contentCountdown={contentCountdown ?? 0}
            surveyData={surveyData}
            setSurveyData={setSurveyData}
            otpData={otpData}
          />
        );
      default:
        return renderDefault();
    }
  }
};

export default AdRenderer;
